import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '@/redux/store';
import { TodoAPI } from '@/services/api/todo/todo';
import {
  ActionCard,
  Button,
  ContentLink,
  DeterminateBadge,
  Divider,
  DropdownButton,
  EmptyState,
  PaginationBar,
  SpinningLoader,
  Typography,
} from 'br2s-ui-componentlibrary_r2_02';
import {
  LocalStorageService,
  reportDtCustomError,
  useHTTPError,
} from 'br2s-ui-common-utils-02';
import { SystemIcon } from 'br2s-ui-componentlibrary_r2_02/dist/foundation/assets/system-icon';
import {
  formatDateLabel,
  getDueStatus,
  renderButtons,
} from '@/utilities/utilities';
import DashboardImg from '@/assets/dashboard2.png';
import GlassCard from '@/components/GlassCard/GlassCard';

import './ToDo.scss';
import { todoStaticDateText } from './todo-constants';
import { TRANSACTIONS } from '../../components/MegaMenu/megaMenuConstants/transactions';

interface TodoItem {
  todoItemId: string;
  title: string;
  primaryAction?: string;
  primaryRedirectUrl?: string;
  secondaryAction?: string;
  secondaryRedirectUrl?: string;
  priority: string;
  dueDate: string;
}

const ToDo = () => {
  const navigate = useNavigate();
  const { toggleLoading, auth } = useStore();
  const { handleHTTPError } = useHTTPError();
  const DASHBORD_IMG_HEIGHT = 696;
  const { uen, cspIndicator, uenName, origin } =
    LocalStorageService.getAuthInfo('uen', 'cspIndicator', 'uenName', 'origin');

  const [todoData, setTodoData] = useState<TodoItem[]>([]);
  const [sortOrder, setSortOrder] = useState('ASC');
  const [paginationData, setPaginationData] = useState({
    items: 0,
    itemsPerPage: 5,
    page: 1,
  });

  const CONTENT_LINKS = renderButtons(
    origin ?? '',
    cspIndicator ?? '',
    auth.hasCspProfile,
  );

  // istanbul ignore next
  useEffect(() => {
    getToDoItem();
  }, [sortOrder, paginationData.page, paginationData.itemsPerPage]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // istanbul ignore next
  const getToDoItem = async () => {
    toggleLoading(true);
    try {
      const response = await TodoAPI.todoItems(
        sortOrder,
        paginationData.itemsPerPage,
        paginationData.page - 1,
      );
      if (response.status === 200) {
        const result = response.data.result;
        setPaginationData((data) => ({
          ...data,
          items: result.totalCount,
        }));
        setTodoData(result.todoList);
      }
    } catch (error) {
      console.error(error);
      handleHTTPError(error);
      reportDtCustomError('GenericHandledError', JSON.stringify(error));
    } finally {
      toggleLoading(false);
    }
  };

  const redirectUrl = (url: string) => {
    if (url.includes('http')) {
      window.open(url, '_blank');
    } else {
      navigate(url);
    }
  };

  const renderTodoHeader = () => {
    if (auth.hasCspProfile === undefined) return <SpinningLoader />;

    return (
      <>
        {origin === 'CP' && (
          <>
            <div className="details-cont">
              <div>
                <Typography
                  type="headline-3"
                  label={uenName as string}
                  weight="bold"
                />
                <Typography type="subheadline" label={uen as string} />
              </div>
              <Button
                id="entity-details"
                type="quiet"
                label="View entity details"
                onClick={() =>
                  navigate(TRANSACTIONS['MANAGE']['updateEntity'].href)
                }
              />
            </div>
            <Divider id="vert-div" type="vertical" />
            <Divider id="hor-div" type="horizontal" />
          </>
        )}
        <div className="todo-links-cont">
          {CONTENT_LINKS.map(({ href, headerKey, ...props }) => (
            <ContentLink
              key={headerKey}
              {...props}
              tag="button"
              onClick={() => navigate(href)}
            />
          ))}
        </div>
      </>
    );
  };

  return (
    <div className="page--container">
      <div className="page--body container">
        <div className="header">
          <div className="header--text-container">
            <Typography
              type="headline-1"
              weight="bold"
              label="Welcome to your dashboard!"
            />
            <Typography
              type="headline-6"
              label="Access all your latest messages, manage your to-do list, and view recent transactions all in one place."
            />
          </div>
          <div className="header--image-container">
            <img
              src={DashboardImg}
              height={DASHBORD_IMG_HEIGHT}
              alt="office-people"
            />
          </div>
        </div>
        <div className="todo-container container">
          <GlassCard isGlassOnly>
            {LocalStorageService.hasAuth() && (
              <div className="card todo-header">{renderTodoHeader()}</div>
            )}
            <div className="card-section-container card">
              <div>
                <div className="pending-actions-cont">
                  <Typography
                    type="headline-3"
                    weight="bold"
                    label="Pending actions"
                  />
                  <DeterminateBadge
                    id="pending-count"
                    value={paginationData.items}
                  />
                </div>
                <Typography
                  type="subheadline"
                  label={`You have ${paginationData.items} items pending your action.`}
                />
                <Typography type="subheadline" label={todoStaticDateText} />
              </div>
              {todoData.length > 0 ? (
                <>
                  <div className="sort-container">
                    <DropdownButton
                      type="quiet"
                      iconType="prepended"
                      dropdownPosition="right"
                      size="medium"
                      id="sort-todo"
                      label={
                        sortOrder === 'DESC'
                          ? 'Sort by date: Latest'
                          : 'Sort by date: Oldest'
                      }
                      iconClassName={SystemIcon.ActionSort}
                      dropdownItems={[
                        {
                          label: 'Date: oldest - latest',
                          isSelected: sortOrder === 'ASC' ? true : false,
                          onClick: () => setSortOrder('ASC'),
                        },
                        {
                          label: 'Date: latest - oldest',
                          isSelected: sortOrder === 'DESC' ? true : false,
                          onClick: () => setSortOrder('DESC'),
                        },
                      ]}
                    />
                  </div>
                  <div className="todo-cards">
                    {todoData.map(
                      ({
                        todoItemId,
                        title,
                        dueDate,
                        priority,
                        primaryAction,
                        secondaryAction,
                        primaryRedirectUrl,
                        secondaryRedirectUrl,
                      }) => {
                        return (
                          <div key={`${title}-${todoItemId}`} className="w-100">
                            <ActionCard
                              id="todo-action-card"
                              cardSize={'xlarge'}
                              title={title}
                              metadata={[
                                `Due on ${formatDateLabel(dueDate)}`,
                                getDueStatus(dueDate),
                              ]}
                              {...(priority === 'true' && {
                                inlineIndicator: {
                                  type: 'error',
                                  label: 'Important',
                                },
                              })}
                              actionButtons={
                                primaryAction && secondaryAction
                                  ? [
                                      {
                                        id: 'secondary-button',
                                        type: 'secondary',
                                        label: secondaryAction,
                                        onClick: () =>
                                          redirectUrl(secondaryRedirectUrl!),
                                      },
                                      {
                                        id: 'primary-button',
                                        type: 'primary',
                                        label: primaryAction,
                                        onClick: () =>
                                          redirectUrl(primaryRedirectUrl!),
                                      },
                                    ]
                                  : [
                                      {
                                        id: 'single-button',
                                        type: secondaryAction
                                          ? 'secondary'
                                          : 'primary',
                                        label: secondaryAction
                                          ? secondaryAction
                                          : primaryAction,
                                        onClick: () =>
                                          redirectUrl(
                                            secondaryAction
                                              ? secondaryRedirectUrl!
                                              : primaryRedirectUrl!,
                                          ),
                                      },
                                    ]
                              }
                              informationSnippet={{ data: [], id: 'info-none' }}
                              isActionButtonsIcon={false}
                            />
                          </div>
                        );
                      },
                    )}
                  </div>
                  <div className="todo-pagination">
                    <PaginationBar
                      id="todo-pagination"
                      data={paginationData}
                      setData={setPaginationData}
                      itemsPerPageItem={['5', '10', '15', '20']}
                    />
                  </div>
                </>
              ) : (
                <div className="w-100">
                  <EmptyState
                    title="There are no pending actions"
                    id="todo-list-empty-state"
                    icon={SystemIcon.GeneralEmpty}
                  />
                </div>
              )}
            </div>
          </GlassCard>
        </div>
      </div>
    </div>
  );
};

export default ToDo;
